import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Eye, Shield, Clock, Star, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { getReviews } from '../services/api';
import Banner1 from '../assets/Banner_1.jpeg';
import Banner2 from '../assets/Banner_2.jpeg';
import Banner3 from '../assets/Banner_3.jpeg';
import Eyeglasses from '../assets/Eyeglasses.jpeg';
import Sunglasses from '../assets/Sunglasses.jpeg';
import Lenses from '../assets/Lenses.jpeg';
const Home = () => {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState(null);
  
  const banners = [
    {
      id: 1,
      image: Banner1,
      title: 'SEE THE WORLD DIFFERENTLY',
      subtitle: 'Exclusive Collection'
    },
    {
      id: 2,
      image: Banner2,
      title: 'Redefine Your Style',
      subtitle: 'MUGHAL AINAK MAHAL'
    },
    {
      id: 3,
      image: Banner3,
      title: 'Premium Eyewear',
      subtitle: 'Discover Your Perfect Vision'
    }
  ];

  // Fetch reviews from backend
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setReviewsLoading(true);
        const response = await getReviews();
        
        if (response.data && response.data.length > 0) {
          // Transform backend data to match frontend format
          const transformedReviews = response.data.slice(0, 3).map(review => ({
            name: `${review.firstName} ${review.lastName}`,
            rating: review.rating,
            comment: review.review,
            location: 'Mughalpura, Lahore', // Default location
            date: review.date || review.createdAt
          }));
          setTestimonials(transformedReviews);
        } else {
          // Fallback to hardcoded testimonials if no reviews exist
          setTestimonials([
            {
              name: 'Ahmed Khan',
              rating: 5,
              comment: 'Excellent service and quality products. Highly recommended!',
              location: 'Mughalpura',
              date: new Date().toISOString()
            },
            {
              name: 'Fatima Ali',
              rating: 5,
              comment: 'Best eyewear shop in the area. Professional staff and great prices.',
              location: 'Lahore',
              date: new Date().toISOString()
            },
            {
              name: 'Muhammad Hassan',
              rating: 5,
              comment: 'Found my perfect glasses here. Very satisfied with the service.',
              location: 'Mughalpura',
              date: new Date().toISOString()
            }
          ]);
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setReviewsError('Failed to load reviews');
        // Fallback to hardcoded testimonials on error
        setTestimonials([
          {
            name: 'Ahmed Khan',
            rating: 5,
            comment: 'Excellent service and quality products. Highly recommended!',
            location: 'Mughalpura',
            date: new Date().toISOString()
          },
          {
            name: 'Fatima Ali',
            rating: 5,
            comment: 'Best eyewear shop in the area. Professional staff and great prices.',
            location: 'Lahore',
            date: new Date().toISOString()
          },
          {
            name: 'Muhammad Hassan',
            rating: 5,
            comment: 'Found my perfect glasses here. Very satisfied with the service.',
            location: 'Mughalpura',
            date: new Date().toISOString()
          }
        ]);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Auto-rotate banners
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000); // Change banner every 5 seconds

    return () => clearInterval(interval);
  }, [banners.length]);

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const featuredCategories = [
    {
      id: 1,
      name: 'Eyeglasses',
      image: Eyeglasses,
      description: 'Premium prescription frames for every style',
      path: '/eyeglasses'
    },
    {
      id: 2,
      name: 'Sunglasses',
      image: Sunglasses,
      description: 'Stylish sunglasses for every occasion',
      path: '/sunglasses'
    },
    {
      id: 3,
      name: 'Lenses',
      image: Lenses,
      description: 'Advanced lens technology for clear vision',
      path: '/lenses'
    }
  ];

  const services = [
    {
      icon: <Eye className="w-8 h-8" />,
      title: 'Eye Examinations',
      description: 'Professional eye tests with certified optometrists'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Quality Assurance',
      description: 'All products come with warranty and quality guarantee'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Quick Service',
      description: 'Fast frame fitting and lens replacement services'
    }
  ];


  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Mughal Ainak Mahal - Premium Eyewear Shop</title>
        <meta name="description" content="Mughal Ainak Mahal - Your trusted partner for quality eyeglasses, sunglasses, and lenses in Mughalpura, Lahore. Professional eye care services and premium eyewear." />
      </Helmet>

      {/* Hero Banner Section */}
      <section className="relative w-full h-0 pb-[39.37%] overflow-hidden">
        {/* Banner Carousel */}
        <div className="absolute inset-0 w-full h-full">
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentBanner ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={banner.image}
                alt={`Banner ${banner.id}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80';
                }}
              />
              
              {/* Banner Content - Shop Now Button Bottom Left */}
              <div className="absolute bottom-4 left-4 sm:bottom-8 sm:left-8 md:bottom-12 md:left-12">
              <Link
                to="/eyeglasses"
                  className="inline-block bg-white text-primary px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-4 rounded-lg font-semibold text-sm sm:text-base md:text-lg hover:bg-gray-100 transition duration-300 transform hover:scale-105"
              >
                Shop Now
              </Link>
              </div>
            </div>
          ))}
          </div>

        {/* Navigation Buttons */}
        <button
          onClick={prevBanner}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 sm:p-3 rounded-full transition-all duration-300 z-10"
          aria-label="Previous banner"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
        </button>
        
        <button
          onClick={nextBanner}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 sm:p-3 rounded-full transition-all duration-300 z-10"
          aria-label="Next banner"
        >
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
        </button>

        {/* Banner Indicators */}
        <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentBanner(index)}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                index === currentBanner
                  ? 'bg-white'
                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              Our Premium Collection
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore our wide range of eyewear options designed for your comfort and style
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {featuredCategories.map((category) => (
              <div
                key={category.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition duration-300 transform hover:-translate-y-2"
              >
                <div className="relative h-[35rem]">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                    <h3 className="text-2xl font-bold mb-4 text-white">{category.name}</h3>
                  <Link
                    to={category.path}
                      className="inline-block bg-white text-primary py-3 px-6 rounded-lg font-semibold hover:bg-gray-100 transition duration-300"
                  >
                    Shop Now
                  </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              Why Choose Mughal Ainak Mahal?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We provide professional eye care services with the highest quality standards
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4 text-primary">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-secondary">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </div>
          
          {reviewsLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-lg text-gray-600">Loading customer reviews...</p>
            </div>
          ) : reviewsError ? (
            <div className="text-center py-12">
              <p className="text-red-500 text-lg">{reviewsError}</p>
              <p className="text-gray-600 mt-2">Showing sample testimonials</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition duration-300 relative min-h-[200px]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary bg-opacity-10 rounded-full flex items-center justify-center text-primary font-semibold">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div className="ml-3">
                        <h3 className="font-semibold text-secondary">{testimonial.name}</h3>
                      </div>
                    </div>
                    <span className="text-green-500 text-sm font-medium">✓ Verified</span>
                  </div>

                  <div className="flex items-center mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < testimonial.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="text-sm text-gray-500 ml-2">({testimonial.rating})</span>
                  </div>

                  <p className="text-gray-600 mb-4 italic pr-4">
                    "{testimonial.comment.length > 100 
                      ? testimonial.comment.substring(0, 100) + '...' 
                      : testimonial.comment}"
                  </p>

                  <div className="absolute bottom-4 left-6 flex items-center text-sm text-gray-500">
                    <CalendarDays className="w-4 h-4 mr-1" />
                    {testimonial.date ? new Date(testimonial.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'Recent'}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="text-center mt-8">
            <Link
              to="/reviews"
              className="inline-block bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-dark transition duration-300"
            >
              Read More Reviews
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home; 